// src/components/GatherLens.jsx
import React, { lazy, Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase/firebaseConfig';
import { onAuthStateChanged, signInAnonymously, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc, onSnapshot } from 'firebase/firestore';

// React Icons
import { FaBars, FaTimes, FaFacebookF, FaInstagram, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { SiX } from 'react-icons/si'; // X icon for Twitter/X

// Framer Motion
import { motion, AnimatePresence } from 'framer-motion';

// Pages
import HomePage from './HomePage';
import AuthModal from './AuthModal';
import { trackScreenView } from '../utils/tracking';
import { DEFAULT_BILLING_CONFIG, normalizeBillingConfig } from '../utils/billing';

const DashboardHub = lazy(() => import('./DashboardHub'));
const CreateEventPage = lazy(() => import('./CreateEventPage'));
const EventDashboard = lazy(() => import('./EventDashboard'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const TermsOfServicePage = lazy(() => import('./TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage'));
const AboutUsPage = lazy(() => import('./AboutUsPage'));
const HelpCenterPage = lazy(() => import('./HelpCenterPage'));
const ContactUsPage = lazy(() => import('./ContactUsPage'));
const GuestCollectorPage = lazy(() => import('./GuestCollectorPage'));
const InfluencerPage = lazy(() => import('./InfluencerPage'));
const SlideshowPublic = lazy(() => import('./SlideshowPublic'));
const PasswordResetPage = lazy(() => import('./PasswordResetPage'));
const EventTypePage = lazy(() => import('./EventTypePage'));
const MarketingComposerPage = lazy(() => import('./MarketingComposerPage'));

// --- Toast Component ---
const Toast = ({ message, type = 'success', onClose }) => {
    const bgColor =
        type === 'error'
            ? 'bg-red-500'
            : type === 'info'
                ? 'bg-blue-500'
                : 'bg-green-500';
    return (
        <AnimatePresence>
            {message && (
                <motion.div
                    key={message}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className={`pointer-events-auto flex w-full max-w-lg items-start gap-3 rounded-xl px-4 py-3 text-white shadow-lg sm:w-auto sm:min-w-[22rem] sm:items-center sm:px-6 ${bgColor}`}
                >
                    {type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}
                    <span className="min-w-0 flex-1 font-semibold leading-6">{message}</span>
                    {type === 'error' && (
                        <button
                            onClick={onClose}
                            className="ml-auto shrink-0 rounded-md p-1 text-white hover:bg-white/10 hover:text-gray-200"
                            aria-label="Dismiss notification"
                        >
                            <FaTimes />
                        </button>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const PageLoader = () => (
    <div className="flex min-h-[55vh] flex-col items-center justify-center gap-4" role="status">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--gold)] border-t-transparent" />
        <p className="text-sm font-medium text-gray-500">Preparing your GatherLens workspace…</p>
    </div>
);

export default function GatherLensApp({ initialUser = null, authError = null, authFallbackTriggered = false }) {
    const [page, setPage] = useState('home');
    const [eventId, setEventId] = useState(null);
    const [user, setUser] = useState(initialUser);
    const [isAnonymous, setIsAnonymous] = useState(Boolean(initialUser?.isAnonymous));
    const [isLoadingAuth, setIsLoadingAuth] = useState(!initialUser && !authFallbackTriggered);
    const [authInitError, setAuthInitError] = useState(authError);
    const [forcedAuthFallback, setForcedAuthFallback] = useState(authFallbackTriggered);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [routeParams, setRouteParams] = useState({});
    const [billingConfig, setBillingConfig] = useState(DEFAULT_BILLING_CONFIG);
    const [, setBillingClock] = useState(() => Date.now());
    const mobileMenuRef = useRef(null);
    const suppressLoginToast = useRef(false);
    const fallbackToastShown = useRef(false);
    const errorToastShown = useRef(false);
    // --- const prevUser = useRef(null); ---

    const routeParamsKey = JSON.stringify(routeParams || {});
    const isSignedIn = !!user && !isAnonymous;
    const isVerifiedUser = isSignedIn && Boolean(user?.emailVerified);
    const isPendingVerification = isSignedIn && !user?.emailVerified;
    const isAuthRestricted = isPendingVerification;

    useEffect(() => {
        const interval = window.setInterval(() => setBillingClock(Date.now()), 60_000);
        return () => window.clearInterval(interval);
    }, []);

    useEffect(() => {
        const billingRef = doc(db, 'appSettings', 'billing');
        const unsubscribe = onSnapshot(
            billingRef,
            (snapshot) => {
                setBillingConfig(normalizeBillingConfig(snapshot.exists() ? snapshot.data() : null));
            },
            (error) => {
                console.warn('Billing settings unavailable; using the default Event Pass price.', error);
                setBillingConfig(DEFAULT_BILLING_CONFIG);
            },
        );
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (initialUser) {
            setUser(initialUser);
            setIsAnonymous(Boolean(initialUser.isAnonymous));
            setIsLoadingAuth(false);
        }
    }, [initialUser]);

    useEffect(() => {
        if (authFallbackTriggered) {
            setForcedAuthFallback(true);
            setIsLoadingAuth(false);
        }
    }, [authFallbackTriggered]);

    useEffect(() => {
        if (authError) {
            setAuthInitError(authError);
            setIsLoadingAuth(false);
        }
    }, [authError]);

    useEffect(() => {
        const params = routeParams || {};
        const slug = typeof params.slug === 'string' ? params.slug : '';
        const screenName = page === 'event-type' && slug ? `event-type-${slug}` : page || 'home';
        trackScreenView(screenName);
    }, [page, routeParams, routeParamsKey]);

    // --- Toast functions ---
    const showToast = useCallback((message, type = 'success') => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setToasts(prev => [...prev, { id, message, type }]);

        if (type === 'success' || type === 'info') {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id));
            }, 2500);
        }
    }, []);

    const dismissToast = id => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    const handleVerificationRequired = useCallback((targetEmail = '') => {
        const resolvedEmail = typeof targetEmail === 'string' ? targetEmail : '';
        setUser(prevUser => {
            if (prevUser) {
                const shouldUpdateEmail = resolvedEmail && resolvedEmail !== prevUser.email;
                if (!shouldUpdateEmail && !prevUser.emailVerified) {
                    return prevUser;
                }
                return {
                    ...prevUser,
                    ...(resolvedEmail ? { email: resolvedEmail } : {}),
                    emailVerified: false,
                };
            }
            const current = auth.currentUser;
            if (!current) {
                return prevUser;
            }
            return {
                uid: current.uid,
                email: resolvedEmail || current.email,
                displayName: current.displayName || null,
                emailVerified: false,
            };
        });
        setShowAuthModal(true);
    }, []);

    const handleVerificationSuccess = useCallback((firebaseUser, options = {}) => {
        const type = options?.type || 'email';
        if (type === 'federated') {
            suppressLoginToast.current = true;
            setShowAuthModal(false);
            showToast('Signed in with Google successfully!', 'success');
            if (typeof window !== 'undefined') {
                try {
                    window.sessionStorage.setItem('loginToastShown', 'true');
                } catch (storageError) {
                    console.warn('Failed to persist login toast flag', storageError);
                }
            }
            return;
        }

        setUser(prevUser => {
            if (!prevUser) {
                return prevUser;
            }

            if (prevUser.emailVerified) {
                return prevUser;
            }

            return {
                ...prevUser,
                emailVerified: true,
            };
        });
        const currentUserId = auth.currentUser?.uid;
        if (currentUserId) {
            updateDoc(doc(db, 'users', currentUserId), { emailVerified: true }).catch(error => {
                console.error('Failed to persist email verification flag', error);
            });
        }
        showToast('Email verified! Your dashboard is ready.', 'success');
    }, [showToast]);

    useEffect(() => {
        if (forcedAuthFallback && !authInitError && !fallbackToastShown.current) {
            fallbackToastShown.current = true;
            showToast('Still connecting to GatherLens. Public pages remain available.', 'info');
        }

        if (!forcedAuthFallback) {
            fallbackToastShown.current = false;
        }
    }, [forcedAuthFallback, authInitError, showToast]);

    useEffect(() => {
        if (authInitError && !errorToastShown.current) {
            errorToastShown.current = true;
            showToast('We\'re having trouble signing you in. Some features may be limited.', 'error');
        }

        if (!authInitError) {
            errorToastShown.current = false;
        }
    }, [authInitError, showToast]);

    // --- Authentication Listener ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (!firebaseUser) {
                setUser(null);
                setIsAnonymous(false);
                sessionStorage.removeItem('loginToastShown');
                setAuthInitError(null);
                setShowAuthModal(false);

                try {
                    await signInAnonymously(auth);
                    return;
                } catch (error) {
                    console.error('Error signing in anonymously:', error);
                    setIsLoadingAuth(false);
                    setAuthInitError(error);
                    return;
                }
            }

            const isAnonymous = firebaseUser.isAnonymous;
            setIsAnonymous(isAnonymous);
            setForcedAuthFallback(false);
            setAuthInitError(null);
            fallbackToastShown.current = false;
            errorToastShown.current = false;

            let emailVerified = false;
            let userData = {};

            if (!isAnonymous) {
                const userRef = doc(db, 'users', firebaseUser.uid);
                const userSnap = await getDoc(userRef);

                if (userSnap.exists()) {
                    userData = userSnap.data() || {};
                } else {
                    const initialDoc = {
                        email: firebaseUser.email,
                        role: 'user',
                        createdAt: serverTimestamp(),
                        emailVerified: Boolean(firebaseUser.emailVerified),
                    };
                    await setDoc(userRef, initialDoc);
                    userData = {
                        email: firebaseUser.email,
                        role: 'user',
                        emailVerified: Boolean(firebaseUser.emailVerified),
                    };
                }
                emailVerified = Boolean(
                    userData.emailVerified || firebaseUser.emailVerified
                );
                userData = {
                    ...userData,
                    emailVerified,
                };
                setUser({
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName,
                    ...userData,
                });
            } else {
                setUser({ uid: firebaseUser.uid });
            }

            if (!isAnonymous && !emailVerified) {
                setShowAuthModal(true);
            } else {
                setShowAuthModal(false);
            }

            // Only show toast if not anonymous and not shown yet this session
            const toastKey = 'loginToastShown';
            if (
                !isAnonymous &&
                emailVerified &&
                !sessionStorage.getItem(toastKey) &&
                !suppressLoginToast.current
            ) {
                showToast('Logged in successfully!', 'success');
                sessionStorage.setItem(toastKey, 'true');
            }

            suppressLoginToast.current = false;

            setIsLoadingAuth(false);
        });

        return () => unsubscribe();
    }, [showToast]);


    // --- Routing Logic ---
    useEffect(() => {
        const handleHashChange = () => {
            let hashValue = window.location.hash ? window.location.hash.substring(1) : '';

            const searchParams = new URLSearchParams(window.location.search);
            const modeParam = searchParams.get('mode');
            const oobCodeParam = searchParams.get('oobCode');
            const langParam = searchParams.get('lang') || searchParams.get('hl');

            if (modeParam === 'resetPassword' && oobCodeParam) {
                const normalizedParams = new URLSearchParams();
                normalizedParams.set('oobCode', oobCodeParam);
                normalizedParams.set('mode', modeParam);
                if (langParam) {
                    normalizedParams.set('lang', langParam);
                }
                const normalizedHash = `reset-password?${normalizedParams.toString()}`;
                const newUrl = `${window.location.origin}${window.location.pathname}#${normalizedHash}`;
                window.history.replaceState({}, '', newUrl);
                hashValue = normalizedHash;
            }

            const [pathPart = '', queryString = ''] = (hashValue || '').split('?');
            const [rawRoute, id] = pathPart.split('/');
            const route = rawRoute || 'home';

            const params = {};
            if (queryString) {
                const search = new URLSearchParams(queryString);
                search.forEach((value, key) => {
                    params[key] = value;
                });
            }
            if (id) {
                params.slug = id;
            }
            params.route = route;
            setRouteParams(params);

            const isGuestPage = route === 'event' && id;
            const restrictedRoutes = ['hub', 'dashboard', 'create-event', 'admin', 'marketing-composer'];

            // Protect owner-only routes if not authenticated or pending verification
            if ((!user || isAnonymous || isAuthRestricted) && restrictedRoutes.includes(route)) {
                setPage('home');
                setEventId(null);
                navigate('home');
                return;
            }

            if (route === 'reset-password') {
                setEventId(null);
                setPage('reset-password');
            }
            else if (route === 'guest' || isGuestPage) { setPage('guest'); setEventId(id || null); }
            else if (route === 'dashboard' && id) { setEventId(id); setPage('dashboard'); }
            else if (route === 'hub') setPage('hub');
            else if (route === 'create-event') setPage('create-event');
            else if (route === 'admin' && user?.role === 'admin') setPage('admin');
            else if (route === 'marketing-composer') {
                if (user?.role === 'admin') {
                    setPage('marketing-composer');
                } else if (!isLoadingAuth) {
                    setPage('home');
                    navigate('home');
                }
            }
            else if (route === 'terms') setPage('terms');
            else if (route === 'privacy') setPage('privacy');
            else if (route === 'about') setPage('about');
            else if (route === 'help') setPage('help');
            else if (route === 'influencers') setPage('influencers');
            else if (route === 'contact') setPage('contact');
            else if (route === 'events' && id) {
                setEventId(null);
                setPage('event-type');
            }
            else if (route === 'slideshow' && id) { // ⟵ NEW: public slideshow route
                setEventId(id);
                setPage('slideshow');
            }
            else setPage('home');
        };

        window.addEventListener('hashchange', handleHashChange);
        handleHashChange();
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [user, isAnonymous, isLoadingAuth, isAuthRestricted]);

    // --- Redirect Logic ---
    useEffect(() => {
        if (!isLoadingAuth) {
            const publicPages = ['home', 'terms', 'privacy', 'about', 'help', 'contact', 'guest', 'influencers', 'slideshow', 'reset-password', 'event-type']; // ⟵ public-only routes
            if (!isSignedIn || isAuthRestricted) {
                if (!publicPages.includes(page)) {
                    setPage('home');
                    navigate('home');
                }
            } else {
                if (page === 'home') {
                    setPage('hub');
                    navigate('hub');
                }
            }
        }
    }, [isSignedIn, isAuthRestricted, isLoadingAuth, page]);

    useEffect(() => {
        if (typeof window === 'undefined' || page === 'slideshow') {
            return undefined;
        }

        const rafId = window.requestAnimationFrame(() => {
            try {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            } catch (error) {
                window.scrollTo(0, 0);
            }

            const docEl = document.documentElement;
            if (docEl) {
                docEl.scrollTop = 0;
                docEl.scrollLeft = 0;
            }

            if (document.body) {
                document.body.scrollTop = 0;
                document.body.scrollLeft = 0;
            }
        });

        return () => window.cancelAnimationFrame(rafId);
    }, [page, eventId, routeParamsKey]);

    const navigate = (path) => {
        window.location.hash = path;
        setMobileNavOpen(false);
    };

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 100; // offset for sticky header
            window.scrollTo({ top, behavior: 'smooth' });
        }
        setMobileNavOpen(false);
    };

    const handleSignOut = async () => {
        try {
            suppressLoginToast.current = true; // suppress only the next auth change
            await signOut(auth);
            setIsAnonymous(false);
            navigate('home');
            showToast('Signed out successfully!', 'info');
        } catch (error) {
            console.error('Error signing out:', error);
            showToast('Error signing out.', 'error');
        }
    };


    // --- Render Page ---
    const renderPage = () => {
        if (isLoadingAuth)
            return (
                <div className="flex items-center justify-center h-screen">
                    <div className="h-12 w-12 border-4 border-[var(--gold)] border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                </div>
            );

        const pageProps = { navigate, user, showToast, eventId, routeParams, billingConfig };

        const renderVerificationRequired = () => (
            <div className="min-h-[60vh] flex flex-col items-center justify-center bg-slate-50 px-6 py-16 text-center">
                <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4">Verify your email to continue</h2>
                <p className="text-gray-600 max-w-2xl">
                    We sent a six-digit verification code to {user?.email || 'your email address'}. Enter that code to unlock your GatherLens dashboard and keep your galleries secure.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={() => setShowAuthModal(true)}
                        className="btn-gold px-6 py-3 text-lg font-semibold"
                    >
                        Enter verification code
                    </button>
                    <button
                        type="button"
                        onClick={handleSignOut}
                        className="px-6 py-3 text-lg font-semibold border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                    >
                        Use a different account
                    </button>
                </div>
            </div>
        );

        const requireVerifiedUser = (content) => {
            if (isVerifiedUser) {
                return content;
            }
            if (!isSignedIn) {
                return <HomePage {...pageProps} onGetStarted={() => setShowAuthModal(true)} />;
            }
            return renderVerificationRequired();
        };

        switch (page) {
            case 'dashboard':
                return requireVerifiedUser(<EventDashboard {...pageProps} />);
            case 'hub':
                return requireVerifiedUser(<DashboardHub {...pageProps} />);
            case 'create-event':
                return requireVerifiedUser(<CreateEventPage {...pageProps} />);
            case 'admin':
                return requireVerifiedUser(user?.role === 'admin' ? <AdminDashboard {...pageProps} /> : <HomePage {...pageProps} onGetStarted={() => setShowAuthModal(true)} />);
            case 'terms':
                return <TermsOfServicePage {...pageProps} />;
            case 'privacy':
                return <PrivacyPolicyPage {...pageProps} />;
            case 'about':
                return <AboutUsPage {...pageProps} onGetStarted={() => setShowAuthModal(true)} />;
            case 'help':
                return <HelpCenterPage {...pageProps} onGetStarted={() => setShowAuthModal(true)} />;
            case 'contact':
                return <ContactUsPage {...pageProps} onGetStarted={() => setShowAuthModal(true)} />;
            case 'influencers':
                return <InfluencerPage {...pageProps} />;
            case 'guest':
                return <GuestCollectorPage {...pageProps} />;
            case 'slideshow': // ⟵ NEW
                return <SlideshowPublic {...pageProps} />;
            case 'event-type':
                return (
                    <EventTypePage
                        {...pageProps}
                        eventType={routeParams?.slug || ''}
                        onGetStarted={() => setShowAuthModal(true)}
                    />
                );
            case 'marketing-composer':
                return requireVerifiedUser(<MarketingComposerPage {...pageProps} />);
            case 'reset-password':
                return (
                    <PasswordResetPage
                        {...pageProps}
                        params={routeParams}
                        onGoHome={() => navigate('home')}
                        onOpenAuth={() => setShowAuthModal(true)}
                    />
                );
            case 'home':
            default:
                return <HomePage {...pageProps} onGetStarted={() => setShowAuthModal(true)} />;
        }
    };

    const isSlideshow = page === 'slideshow';
    const isMarketingComposerPage = page === 'marketing-composer';
    const isEventsLanding = page === 'event-type';
    const staticContentPages = ['about', 'help', 'contact', 'influencers', 'terms', 'privacy'];
    const hideAppChrome = isSlideshow || isMarketingComposerPage;

    return (
        <>
            <style>{`
                .social-icon { transition: transform 0.3s ease, color 0.3s ease; }
                .social-icon:hover { transform: translateY(-3px) scale(1.2); color: var(--gold); }
                .slide-down {
                    max-height: 0;
                    overflow: hidden;
                    padding-top: 0;
                    padding-bottom: 0;
                    opacity: 0;
                    transform: translateY(-8px);
                    pointer-events: none;
                    transition: max-height 0.45s ease, padding 0.45s ease, opacity 0.35s ease, transform 0.35s ease;
                }
                .slide-down.open {
                    max-height: 520px;
                    padding-top: 1rem;
                    padding-bottom: 1.5rem;
                    opacity: 1;
                    transform: translateY(0);
                    pointer-events: auto;
                }
                @keyframes fade-in { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fade-in { animation: fade-in 0.3s ease forwards; }
            `}</style>

            <div className={`min-h-screen font-sans flex flex-col ${isSlideshow ? 'bg-black' : 'bg-[var(--off-white)]'}`}>

                {/* --- Toast Container --- */}
                {!hideAppChrome && (
                    <div className="pointer-events-none fixed inset-x-4 top-4 z-50 flex flex-col items-center gap-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2">
                        {toasts.map(toast => (
                            <Toast
                                key={toast.id}
                                message={toast.message}
                                type={toast.type}
                                onClose={() => dismissToast(toast.id)}
                            />
                        ))}
                    </div>
                )}

                {/* --- Global Auth Status Banner --- */}
                {!hideAppChrome && (forcedAuthFallback || authInitError) && (
                    <div
                        className={`text-sm text-center font-medium py-2 px-4 ${authInitError ? 'bg-red-600 text-white' : 'bg-amber-400 text-slate-900'}`}
                    >
                        {authInitError
                            ? 'We were unable to verify your sign-in. You can continue browsing public content while we retry.'
                            : 'Still connecting to GatherLens. You can explore public content while we finish signing you in.'}
                    </div>
                )}

                {/* --- Header --- */}
                {!hideAppChrome && (
                    <header className="sticky top-0 z-40 border-b border-black/5 bg-[rgba(248,247,242,0.88)] backdrop-blur-xl">
                        <nav className="container mx-auto max-w-7xl px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3">
                            <a
                                href="#home"
                                onClick={(e) => { e.preventDefault(); navigate(isVerifiedUser ? 'hub' : 'home'); }}
                                className="brand-mark cursor-pointer"
                                aria-label="GatherLens home"
                            >
                                GatherLens
                            </a>

                            {/* Desktop Nav */}
                            <div className="hidden md:flex items-center space-x-8">
                                {isEventsLanding ? (
                                    <a
                                        href="#home"
                                        onClick={(e) => { e.preventDefault(); navigate('home'); }}
                                        className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                    >
                                        Home
                                    </a>
                                ) : (
                                    <>
                                        {user?.role === 'admin' && (
                                            <button onClick={() => navigate('admin')} className="text-gray-700 font-bold hover:text-[var(--gold)]">
                                                Admin
                                            </button>
                                        )}

                                        {staticContentPages.includes(page) ? (
                                            <>
                                                <a
                                                    href="#home"
                                                    onClick={(e) => { e.preventDefault(); navigate('home'); }}
                                                    className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                                >
                                                    Home
                                                </a>
                                                <button onClick={() => setShowAuthModal(true)} className="btn-gold py-2 px-5">
                                                    Get Started
                                                </button>
                                            </>
                                        ) : !isVerifiedUser ? (
                                            <>
                                                <a
                                                    href="#occasions"
                                                    onClick={(e) => { e.preventDefault(); scrollToSection('occasions'); }}
                                                    className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                                >
                                                    Occasions
                                                </a>

                                                <a
                                                    href="#how-it-works"
                                                    onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}
                                                    className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                                >
                                                    How It Works
                                                </a>

                                                <a
                                                    href="#pricing"
                                                    onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}
                                                    className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                                >
                                                    Pricing
                                                </a>

                                                <a
                                                    href="#faq"
                                                    onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}
                                                    className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                                >
                                                    FAQ
                                                </a>
                                                <button onClick={() => setShowAuthModal(true)} className="btn-gold font-bold py-2 px-5">
                                                    Get Started
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => navigate('hub')} className="text-gray-700 font-bold hover:text-[var(--gold)]">My Events</button>
                                                <button onClick={handleSignOut} className="bg-gray-200 text-gray-800 font-bold py-2 px-5 rounded-lg hover:bg-gray-300 transition-colors">
                                                    Sign Out
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Mobile Nav Button */}
                            <div className="md:hidden">
                                <button onClick={() => setMobileNavOpen(!mobileNavOpen)} className="grid h-11 w-11 place-items-center rounded-full text-gray-700 text-xl hover:bg-black/5" aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}>
                                    {mobileNavOpen ? <FaTimes /> : <FaBars />}
                                </button>
                            </div>
                        </nav>

                        {/* Mobile Menu */}
                        <div
                            ref={mobileMenuRef}
                            className={`md:hidden slide-down ${mobileNavOpen ? 'open' : ''} flex flex-col space-y-3 text-center px-4 sm:px-6`}
                        >
                            {isEventsLanding ? (
                                <a
                                    href="#home"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('home');
                                        setMobileNavOpen(false);
                                    }}
                                    className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                >
                                    Home
                                </a>
                            ) : staticContentPages.includes(page) ? (
                                <>
                                    <a
                                        href="#home"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate('home');
                                            setMobileNavOpen(false);
                                        }}
                                        className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                    >
                                        Home
                                    </a>
                                    <button onClick={() => { setShowAuthModal(true); setMobileNavOpen(false); }} className="btn-gold py-2 px-5 mx-auto">Get Started</button>
                                </>
                            ) : !isVerifiedUser ? (
                                <>
                                    <a
                                        href="#occasions"
                                        onClick={(e) => { e.preventDefault(); scrollToSection('occasions'); }}
                                        className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                    >
                                        Occasions
                                    </a>

                                    <a
                                        href="#how-it-works"
                                        onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}
                                        className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                    >
                                        How It Works
                                    </a>

                                    <a
                                        href="#pricing"
                                        onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}
                                        className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                    >
                                        Pricing
                                    </a>

                                    <a
                                        href="#faq"
                                        onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}
                                        className="text-gray-700 font-bold hover:text-[var(--gold)]"
                                    >
                                        FAQ
                                    </a>
                                    <button onClick={() => { setShowAuthModal(true); setMobileNavOpen(false); }} className="btn-gold py-2 font-bold px-5 mx-auto">Get Started</button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => navigate('hub')} className="text-gray-700 font-bold hover:text-[var(--gold)]">My Events</button>
                                    <button onClick={handleSignOut} className="bg-gray-200 text-gray-800 font-bold py-2 px-5 rounded-lg hover:bg-gray-300 transition-colors mx-auto">Sign Out</button>
                                </>
                            )}
                        </div>
                    </header>
                )}

                {/* --- Main Content --- */}
                <main className={`flex-grow ${isSlideshow ? 'bg-black' : ''}`}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={page}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4, ease: 'easeInOut' }}
                        >
                            <Suspense fallback={<PageLoader />}>
                                {renderPage()}
                            </Suspense>
                        </motion.div>
                    </AnimatePresence>
                </main>

                {/* --- Footer --- */}
                {!hideAppChrome && (
                    <footer className="bg-[var(--charcoal)] text-white">
                        <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-12">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 text-center md:text-left">
                                <div className="flex flex-col items-center md:items-start">
                                    <a href="#home" className="brand-mark brand-mark--light mb-4" onClick={(e) => { e.preventDefault(); navigate(isVerifiedUser ? 'hub' : 'home'); }}>
                                        GatherLens
                                    </a>
                                    <div className="mt-2 text-gray-400 text-sm italic text-center">
                                        Made with <span className="text-red-500">♥</span> in the USA
                                    </div>
                                </div>

                                <div className="text-center md:text-left">
                                    <h3 className="font-bold uppercase mb-4 text-center md:text-left">Navigate</h3>
                                    {isEventsLanding ? (
                                        <a
                                            href="#home"
                                            onClick={(e) => { e.preventDefault(); navigate('home'); }}
                                            className="block text-gray-300 hover:text-[var(--gold)] mb-2 text-center md:text-left"
                                        >
                                            Home
                                        </a>
                                    ) : isVerifiedUser ? (
                                        <div className="flex flex-col items-center md:items-start gap-2 text-sm font-medium">
                                            <button
                                                type="button"
                                                onClick={() => navigate('hub')}
                                                className="w-full max-w-[12rem] text-center md:text-left text-gray-300 transition-colors hover:text-[var(--gold)] md:mx-0"
                                            >
                                                My Events
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => navigate('create-event')}
                                                className="w-full max-w-[12rem] text-center md:text-left text-gray-300 transition-colors hover:text-[var(--gold)] md:mx-0"
                                            >
                                                Create Event
                                            </button>
                                            {user?.role === 'admin' && (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('admin')}
                                                    className="w-full max-w-[12rem] text-center md:text-left text-gray-300 transition-colors hover:text-[var(--gold)] md:mx-0"
                                                >
                                                    Admin Dashboard
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={handleSignOut}
                                                className="w-full max-w-[12rem] text-center md:text-left text-gray-300 transition-colors hover:text-[var(--gold)] md:mx-0"
                                            >
                                                Sign Out
                                            </button>
                                        </div>
                                    ) : staticContentPages.includes(page) ? (
                                        <a
                                            href="#home"
                                            onClick={(e) => { e.preventDefault(); navigate('home'); }}
                                            className="block text-gray-300 hover:text-[var(--gold)] mb-2 text-center md:text-left"
                                        >
                                            Home
                                        </a>
                                    ) : (
                                        <>
                                            <a
                                                href="#occasions"
                                                onClick={(e) => { e.preventDefault(); scrollToSection('occasions'); }}
                                                className="block text-gray-300 hover:text-[var(--gold)] mb-2 text-center md:text-left"
                                            >
                                                Occasions
                                            </a>

                                            <a
                                                href="#pricing"
                                                onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}
                                                className="block text-gray-300 hover:text-[var(--gold)] mb-2 text-center md:text-left"
                                            >
                                                Pricing
                                            </a>

                                            <a
                                                href="#faq"
                                                onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}
                                                className="block text-gray-300 hover:text-[var(--gold)] mb-2 text-center md:text-left"
                                            >
                                                FAQ
                                            </a>

                                        </>
                                    )}
                                </div>

                                <div>
                                    <h3 className="font-bold uppercase mb-4">Company</h3>
                                    <a href="#about" className="block text-gray-300 hover:text-[var(--gold)] mb-2">About Us</a>
                                    <a href="#help" className="block text-gray-300 hover:text-[var(--gold)] mb-2">Help Center</a>
                                    <a href="#contact" className="block text-gray-300 hover:text-[var(--gold)] mb-2">Contact</a>
                                </div>

                                <div>
                                    <h3 className="font-bold uppercase mb-4">Community</h3>
                                    <a href="#influencers" className="block text-gray-300 hover:text-[var(--gold)] mb-2">Influencers</a>
                                </div>

                                <div>
                                    <h3 className="font-bold uppercase mb-4">Legal</h3>
                                    <a href="#terms" className="block text-gray-300 hover:text-[var(--gold)] mb-2">Terms of Service</a>
                                    <a href="#privacy" className="block text-gray-300 hover:text-[var(--gold)] mb-2">Privacy Policy</a>
                                </div>
                            </div>

                            <div className="border-t border-gray-500 mt-8 pt-6 flex flex-col md:flex-row items-center justify-between text-gray-400 text-sm space-y-2 md:space-y-0">
                                <div className="text-center md:text-left">
                                    &copy; {new Date().getFullYear()} GatherLens. All Rights Reserved.
                                </div>
                                <div className="flex space-x-4 justify-center md:justify-end">
                                    <a href="https://www.facebook.com/gatherlens/" target="_blank" rel="noreferrer" className="hover:text-[var(--gold)] transition-colors">
                                        <FaFacebookF />
                                    </a>
                                    <a href="https://www.instagram.com/gatherlens/" target="_blank" rel="noreferrer" className="hover:text-[var(--gold)] transition-colors">
                                        <FaInstagram />
                                    </a>
                                    <a href="https://x.com/GatherLens" target="_blank" rel="noreferrer" className="hover:text-[var(--gold)] transition-colors">
                                        <SiX />
                                    </a>
                                </div>
                            </div>

                        </div>
                    </footer>
                )}

                {/* --- Auth Modal --- */}
                {showAuthModal && !isSlideshow && (
                    <AuthModal
                        onClose={() => setShowAuthModal(false)}
                        onVerificationSuccess={handleVerificationSuccess}
                        onVerificationRequired={handleVerificationRequired}
                        forceVerification={isPendingVerification}
                        verificationEmail={user?.email || ''}
                    />
                )}
            </div>
        </>
    );
}

